import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Select,
  StatusBadge,
  Table,
  Td,
  Th,
  EmailStatusBadge,
} from '../components/ui';
import {
  api,
  type Company,
  type Deal,
  type LifecycleStage,
  type Prospect,
  type TimelineEvent,
} from '../lib/api';
import { date, dateOnly, humanize, moneyIn, relative } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

interface DetailResponse {
  company: Company;
  prospects: Prospect[];
  deals: Deal[];
  timeline: TimelineEvent[];
}

const LIFECYCLE_STAGES: LifecycleStage[] = [
  'lead',
  'engaged',
  'qualified',
  'customer',
  'churned',
  'disqualified',
];

const LIFECYCLE_TONE: Record<LifecycleStage, 'neutral' | 'blue' | 'purple' | 'green' | 'amber'> = {
  lead: 'neutral',
  engaged: 'blue',
  qualified: 'purple',
  customer: 'green',
  churned: 'amber',
  disqualified: 'amber',
};

export function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const detail = useApi<DetailResponse>(id ? `/api/companies/${id}` : null);

  if (detail.loading) return <Loading />;
  if (detail.error) return <ErrorNote message={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  const { company, deals } = detail.data;
  const prospects = detail.data.prospects ?? [];
  const timeline = detail.data.timeline ?? [];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/companies" className="text-xs font-medium text-accent-600 hover:underline">
            ← Companies
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink-900">{company.name}</h1>
          <p className="mt-0.5 text-sm text-ink-600">
            {[company.domain, company.industry].filter(Boolean).join(' · ') || 'No details recorded'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge tone={LIFECYCLE_TONE[company.lifecycle_stage]}>{humanize(company.lifecycle_stage)}</Badge>
            <Badge>{humanize(company.source)}</Badge>
            {company.tags.map((tag) => (
              <Badge key={tag} tone="blue">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <SummaryPanel company={company} onChanged={detail.reload} />
          <Card title={`Timeline (${timeline.length})`} bodyClassName={timeline.length ? 'p-0' : undefined}>
            {timeline.length === 0 ? (
              <p className="text-sm text-ink-500">
                Nothing on record yet — this account's people have no logged activity.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {timeline.map((event) => (
                  <TimelineItem key={event.id} event={event} />
                ))}
              </ul>
            )}
          </Card>

          <Card title={`People (${prospects.length})`} bodyClassName={prospects.length ? 'p-0' : undefined}>
            {prospects.length === 0 ? (
              <EmptyState title="No contacts yet" description="People link to this company automatically." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Title</Th>
                    <Th>Status</Th>
                    <Th>Last touch</Th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((p) => (
                    <tr key={p.id} className="hover:bg-ink-50">
                      <Td>
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <Link to={`/prospects/${p.id}`} className="font-medium text-accent-700 hover:underline">
                            {`${p.first_name} ${p.last_name}`.trim() || p.email || 'Unnamed'}
                          </Link>
                          <EmailStatusBadge status={p.email_status} />
                        </span>
                      </Td>
                      <Td>{p.job_title || '—'}</Td>
                      <Td>
                        <StatusBadge status={p.status} />
                      </Td>
                      <Td>{p.last_touch_at ? relative(p.last_touch_at) : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Details">
            <LifecycleStageControl company={company} onChanged={detail.reload} />
            <dl className="mt-3 space-y-2.5 text-sm">
              <DetailRow label="Domain">{company.domain ?? '—'}</DetailRow>
              <DetailRow label="Industry">{company.industry || '—'}</DetailRow>
              <DetailRow label="Added">{date(company.created_at)}</DetailRow>
              <DetailRow label="Last touch">
                {company.last_touch_at ? relative(company.last_touch_at) : '—'}
              </DetailRow>
            </dl>
            {company.description && <p className="mt-3 text-sm text-ink-700">{company.description}</p>}
          </Card>

          <Card title="Deals">
            {deals.length === 0 ? (
              <p className="text-sm text-ink-500">No deals yet across this account's contacts.</p>
            ) : (
              <ul className="grid gap-2">
                {deals.map((deal) => (
                  <li
                    key={deal.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-ink-50 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">{deal.name || 'Untitled deal'}</p>
                      <p className="text-xs text-ink-500">
                        {moneyIn(deal.value, deal.currency)}
                        {deal.expected_close_date && ` · close ${dateOnly(deal.expected_close_date)}`}
                      </p>
                    </div>
                    <StatusBadge status={deal.stage} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

/** The account briefing, refreshed by the scheduler as activity comes in and on demand here. */
function SummaryPanel({ company, onChanged }: { company: Company; onChanged: () => void }) {
  const refresh = useAction(async () => {
    await api.post(`/api/companies/${company.id}/refresh-summary`);
    onChanged();
  });
  const hasSummary = Boolean(company.summary);

  return (
    <Card
      title="Summary"
      action={
        <Button size="sm" loading={refresh.pending} onClick={() => void refresh.run()}>
          {hasSummary ? 'Refresh' : 'Summarize'}
        </Button>
      }
    >
      {!hasSummary ? (
        <p className="text-sm text-ink-500">
          No summary yet. It refreshes automatically as activity comes in, or generate one now.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-800">{company.summary}</p>
          {company.summary_next_step && (
            <div className="mt-2.5 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-900 ring-1 ring-inset ring-accent-200">
              <span className="font-medium">Next: </span>
              {company.summary_next_step}
              {company.next_step_date && <span className="ml-1 text-accent-700">({dateOnly(company.next_step_date)})</span>}
            </div>
          )}
          {company.summary_updated_at && (
            <p className="mt-2 text-xs text-ink-400">Updated {relative(company.summary_updated_at)}</p>
          )}
        </>
      )}
      {refresh.error && <div className="mt-2"><ErrorNote message={refresh.error} /></div>}
    </Card>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-xs uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className="min-w-0 truncate text-right text-ink-800">{children}</dd>
    </div>
  );
}

function LifecycleStageControl({ company, onChanged }: { company: Company; onChanged: () => void }) {
  const move = useAction(async (stage: LifecycleStage) => {
    if (stage === company.lifecycle_stage) return;
    await api.put(`/api/companies/${company.id}`, { lifecycle_stage: stage });
    onChanged();
  });

  return (
    <Field label="Lifecycle stage">
      <Select
        value={company.lifecycle_stage}
        disabled={move.pending}
        onChange={(event) => void move.run(event.target.value as LifecycleStage)}
      >
        {LIFECYCLE_STAGES.map((stage) => (
          <option key={stage} value={stage}>
            {humanize(stage)}
          </option>
        ))}
      </Select>
      {move.error && <div className="mt-2"><ErrorNote message={move.error} /></div>}
    </Field>
  );
}

const TIMELINE_KIND_LABEL: Record<TimelineEvent['kind'], string> = {
  email_out: 'Email sent',
  email_in: 'Email received',
  linkedin_dm: 'LinkedIn DM',
  linkedin_touch: 'LinkedIn touch',
  note: 'Note',
  call: 'Call',
  meeting: 'Meeting',
  signal: 'Signal',
  deal_stage: 'Deal stage',
  lifecycle_change: 'Stage change',
  system: 'System',
};

const TIMELINE_KIND_TONE: Record<TimelineEvent['kind'], 'neutral' | 'blue' | 'purple' | 'green' | 'amber'> = {
  email_out: 'blue',
  email_in: 'blue',
  linkedin_dm: 'purple',
  linkedin_touch: 'purple',
  note: 'neutral',
  call: 'amber',
  meeting: 'amber',
  signal: 'green',
  deal_stage: 'green',
  lifecycle_change: 'purple',
  system: 'neutral',
};

function TimelineItem({ event }: { event: TimelineEvent }) {
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={TIMELINE_KIND_TONE[event.kind]}>{TIMELINE_KIND_LABEL[event.kind]}</Badge>
        {event.prospect_name && <span className="text-xs text-ink-500">{event.prospect_name}</span>}
        <span className="text-sm font-medium text-ink-800">{event.title || TIMELINE_KIND_LABEL[event.kind]}</span>
        <span className="ml-auto text-xs text-ink-400">{relative(event.occurred_at)}</span>
      </div>
      {event.body && <p className="mt-1.5 whitespace-pre-wrap text-sm text-ink-600">{event.body}</p>}
    </li>
  );
}
